import {Button, ContextMenu, Icon, Li, Tooltip} from '@startlibs/components';
import {getColor, isSmallDevice, preventDefault} from '@startlibs/utils'
import {usePopupToggle, useToggle} from '@startlibs/core';
import React from 'react'
import {FormattedMessage} from 'react-intl'
import _ from 'lodash/fp'
import styled, {css} from 'styled-components'
import {AdminCaseCardCategories, CategoryTag} from '../dashboard/AdminCaseCardCategories'
import {
  CASE_ARCHIVED,
  CASE_CLOSED,
  CASE_DRAFT,
  CASE_REJECTED,
  CASE_REVIEWED,
  PENDING_ASSIGNMENT,
  PENDING_REQUEST,
  REQUEST_REJECTED,
  UNDER_REVIEW,
  WAITING_ACCEPTANCE,
  WAITING_APPROVAL,
  WAITING_MEDICAL_RECORDS,
  WAITING_MORE_INFORMATION
} from '../../enums/CaseState';
import {ScrollToTop} from '../../components/ScrollToTop'
import {isStateBefore} from '../../request/utils';
import {lazyProviderInfo, lazyProviderInfoSec, lazyUserInfo} from '../../components/WithProvider'
import {useEditCategories} from '../experts/hooks/useEditCategories'
import {useToggleAsPriority} from '../hooks/useMarkAsPriority'
import {useReadOnlyShare} from './hooks/useReadOnlyShare'
import {IfLocale} from '../../hocs/IfLocale'
import {useEditCaseNumber} from '../experts/hooks/useEditCaseNumber';
import {useDeleteCaseDialog} from '../hooks/useDeleteCaseDialog';
import {confirmExitPage} from '../../hooks/useConfirmExitPage';
import {useNavigate} from '@startlibs/components';
import {useAskContributionShare} from './hooks/useAskContributionShare';
import {useShareCompleted} from './hooks/useShareCompleted';
import {REVIEWER_TYPE_ADMIN, SUPER} from '../../enums/UserRole';
import {jwtPostFetcher} from '../../utils/authFetch';
import {getJwt} from '../../hooks/useJwt';
import {setNotification} from '../../components/Notifications';
import {LocaleDisplay} from "../../components/LocaleSelector";
import {
  CaseId, CollapsedSidebarIcon,
  Sidebar,
  SidebarCard,
  SidebarContainer,
  SidebarDesc, SidebarSeparator,
  SidebarTitle,
  StepLink, useMobileSidebar
} from "../../components/Sidebar";
import { useMatch } from '../../hooks/useMatch';
import { ComposeDirectMessageDialog } from '../hooks/ComposeDirectMessageDialog';
import { CoordinatorSelector } from './CoordinatorSelector'

export const PageWrapper = styled.div`
    display: flex;
    justify-content: center;
    ${props => props.responsiveWorkaround && css`
      // min-width: 1024px;
    `}
    ${props => props.noSidebar && css`
      // min-width: 640px;
      min-width: 320px;
    `}
    ${props => props.controlPanel && css`
      max-width: 100%;
      min-width: 710px;
    `}
`

export const PageContent = styled.div`
    flex-grow: 1;
    max-width: 66rem;
    ${props => props.controlPanel && css`
      max-width: 100%;
      min-width: 300px;
      flex-shrink: 1;
    `}
`

const SmallEditButton = styled(Icon).attrs({icon:'edit'})`
  display: inline-block;
  border-radius: 1rem;
  line-height: 1.5rem;
  text-align: center;
  background: ${getColor('gray240')};
  padding: 0 .5rem;
  font-size: 12px;
  white-space: nowrap;
  min-width: 1.5rem;
  vertical-align: -1px;
  cursor: pointer;
  
  &:before {
    content: "✎";
    font-size: 12px;
  }
  
  :hover {
    background: rgba(0,0,0,0.15);
  }
`

const ShareButtonDiv = styled.div`
  display: flex;
  padding: 0 1.5rem;
  margin-top: 0.75rem;
`

export const RequestSidebar = ({isCreatingCase, caseRequest = {}, setCaseRequest, panelToggle = {}, isReport = false}) => {

  const navigate = useNavigate()
  const confirmRedirect = preventDefault((e) => {
    // const href = e.target.href // Not work in this new
    // const href = e.target.getAttribute('href') // Works
    const href = e.target.pathname // Works and already used in other part of system
    confirmExitPage().then(() => navigate(href, {replace: false}))
  })
  
  const userInfo = lazyUserInfo.read()
  const providerInfo = lazyProviderInfo.read()
  const providerInfoSec = lazyProviderInfoSec.read()

  const isMultiLanguage = providerInfo?.preferredLanguage?.options && providerInfo?.preferredLanguage?.options?.length > 1

  const messagingEnabled = providerInfo?.messagingEnabled
  const managePaymentsEnabled = providerInfo?.managePaymentsEnabled

  const adminIsReviewingThisCase = userInfo?.canReviewCases && caseRequest?.isAssignedToLoggedUser && (caseRequest?.state === UNDER_REVIEW || caseRequest?.state === WAITING_MORE_INFORMATION) && caseRequest?.activeCaseExpertType === REVIEWER_TYPE_ADMIN

  const caseDeleteVisibility = providerInfoSec?.caseDeleteVisibility ?? 'DISABLED'
  const isInternalLogin = (login) =>
    login?.endsWith('@purview.net') || login?.endsWith('@healthmark-group.com')
  const canDeleteCase = userInfo.role === SUPER && (
    caseDeleteVisibility === 'SUPER_ADMIN' ||
    (caseDeleteVisibility === 'PURVIEW' && isInternalLogin(userInfo.login))
  )

  const editCategories = useEditCategories(userInfo,(_caseRequest) => (categories) =>
    setCaseRequest(_.set('categories', categories))
  )

  const canAskForContribution = caseRequest && (
    caseRequest.state ===  CASE_DRAFT ||
    caseRequest.state ===  CASE_REVIEWED ||
    caseRequest.state ===  PENDING_ASSIGNMENT ||
    caseRequest.state ===  PENDING_REQUEST ||
    caseRequest.state ===  CASE_REJECTED ||
    caseRequest.state ===  REQUEST_REJECTED ||
    caseRequest.state ===  UNDER_REVIEW ||
    caseRequest.state ===  WAITING_ACCEPTANCE ||
    caseRequest.state ===  WAITING_APPROVAL ||
    caseRequest.state ===  WAITING_MEDICAL_RECORDS ||
    caseRequest.state ===  CASE_ARCHIVED ||
    caseRequest.state ===  WAITING_MORE_INFORMATION)

  const canShareCase = caseRequest && (
    caseRequest.state ===  CASE_CLOSED
  )

  const simpleShare = useReadOnlyShare(caseRequest)
  const [askContribution] = useAskContributionShare(caseRequest)
  const [shareCompleted] = useShareCompleted(caseRequest)

  const toggleAsPriority = useToggleAsPriority((_caseRequest) =>
    setCaseRequest(_.update('markedAsPriorityEpochMilli',(v) => v ? null : Date.now()))
    // setList(_.map(item => item === caseRe
    // quest ? _.update('priority',(p)=>!p) : item))
  )

  const directMessage = useToggle()
  
  const editCaseNumber = useEditCaseNumber((_caseRequest) => (caseNumber) =>
      setCaseRequest(_.set('legalInfo',caseNumber))
  )

  const openDeleteCase = useDeleteCaseDialog(caseRequest, () => navigate("/admin"))

  const isCurrentStateBefore = isStateBefore(caseRequest && caseRequest.state)

  const contextMenuToggle = usePopupToggle()
  const match = useMatch("/admin/request/:userType/:step")
  const step = match?.params && match?.params.step

  const [isCollapsed, expandedToggle] = useMobileSidebar(step)

  const handleDirectMessageClick = async () => {
    await confirmExitPage();
    directMessage.open();
  };

  const sidebarHeader = caseRequest.patientInfo
    ? <>
    <SidebarTitle className='fs-exclude'>{caseRequest.patientInfo.firstName} {caseRequest.patientInfo?.middleName} {caseRequest.patientInfo.lastName}</SidebarTitle>
    <IfLocale contains="LEGAL">
      <CaseId><b>Case Number:</b> {
        caseRequest?.legalInfo?.caseNumber?.length > 0 ?
          <span className="nowrap">{caseRequest.legalInfo.caseNumber} <SmallEditButton onClick={editCaseNumber.willOpenWith(caseRequest)}/></span>
          :
          editCaseNumber
            ? <a className="light-link" onClick={editCaseNumber.willOpenWith(caseRequest)}>Add</a>
            : null
      }
      </CaseId>
    </IfLocale>
    {/* {caseRequest.requestCode && <CaseId><b>Case Id:</b> {caseRequest.requestCode}</CaseId>} */}
    {
      caseRequest.requestCode &&
      <CaseId>
        <b><FormattedMessage defaultMessage="Case Id:" description='RequestSidebar admin caseid item label'/></b>
        <span data-testid='case-id'> {caseRequest.requestCode}</span>
      </CaseId>
    }
    <CoordinatorSelector caseRequest={caseRequest} setCaseRequest={setCaseRequest} userInfo={userInfo} />
    <CaseId>
      <LocaleDisplay locale={caseRequest.caseInfo.preferredLanguage} providerInfo={providerInfo} />
    </CaseId>
  </>
  : <SidebarTitle>New opinion request</SidebarTitle>

  return <SidebarContainer
    isReport={isReport}
    collapsed={isCollapsed}
    onClick={e => e.target === e.currentTarget && expandedToggle.close()}
  >
    <ScrollToTop when={step}/>
    <Sidebar>
      <SidebarCard
        isPriority={caseRequest.markedAsPriorityEpochMilli}
        onClick={isCollapsed ? expandedToggle.open : undefined}
        collapsed={isCollapsed && sidebarHeader}
      >
        {isSmallDevice() && <CollapsedSidebarIcon icon="x" onClick={expandedToggle.close}/>}
        {caseRequest.patientInfo ? <>
            <div className="sidebar-heading">
              <SidebarDesc>CASE DETAILS</SidebarDesc>
              {sidebarHeader}
              <AdminCaseCardCategories
                inSidebar
                categories={caseRequest.categories}
                quantityVisible={1}
                whenEmpty={
                  <CategoryTag onClick={editCategories.willOpenWith(caseRequest)}>Edit categories</CategoryTag>
                }
                editCategories={editCategories.willOpenWith(caseRequest)}
                userInfo={userInfo}
              />
              <div className="sidebar-icons">
              {/* COMMENTING THE OLD SHARE */}
              {providerInfo.readOnlyAccess && !canShareCase &&
                <div onClick={simpleShare} className="share-icon" data-testid="share-icon"> 
                  <Tooltip content='Share case'>
                    <Icon icon='share-line'/>
                  </Tooltip>
                </div>
              }
              {providerInfoSec?.directMessageInfo && <div className="direct-msg-icon" data-testid="direct-message-icon" onClick={handleDirectMessageClick}>
                <Tooltip content={"Compose direct message"}>
                  <Icon icon={"mail-line"}/>
                </Tooltip>
              </div>}
              <div className="priority-icon" onClick={toggleAsPriority.willOpenWith(caseRequest)}>
                <Tooltip content={caseRequest.markedAsPriorityEpochMilli ? "Remove as priority" : "Mark as priority"}>
                  <Icon icon={caseRequest.markedAsPriorityEpochMilli ? "star" : "star-line"}/>
                </Tooltip>
              </div>
              {caseRequest.caseInfo.externalAccessBlocked &&
                <Tooltip css='cursor: auto;' content="Case is locked">
                  <Icon css='cursor: auto; margin-left:0.0rem;' icon="lock"/>
                </Tooltip>
              }
              <Button small onlyDropdown light noFocus onClick={contextMenuToggle.toggle} css="margin-left: 0.25rem;box-shadow:none;" >
                {contextMenuToggle.isOpen
                  && <ContextMenu>
                    <Li disabled={caseRequest.caseInfo.externalAccessBlocked} icon={"copy"} label={"Copy case link"} onClick={() => {
                      const caseLink = `${window.location.origin}/access?code=${caseRequest.requestCode}${(isMultiLanguage && caseRequest?.caseInfo?.preferredLanguage) ? `&locale=${caseRequest?.caseInfo?.preferredLanguage}` : ""}`
                      navigator.clipboard.writeText(caseLink).then(() => setNotification("Copied link to clipboard"))
                    }}/>
                    <Li
                      icon={caseRequest.caseInfo.externalAccessBlocked ? "unlock-line" : "lock-line"}
                      label={caseRequest.caseInfo.externalAccessBlocked ? "Unlock external access" : "Lock external access"}
                      onClick={() => {
                        if(caseRequest.caseInfo.externalAccessBlocked){
                          jwtPostFetcher(getJwt())(`/api/admin/unblockExternalAccess?requestId=${caseRequest.requestId}`)
                            .then((_response) => {
                              setCaseRequest(_.set('caseInfo.externalAccessBlocked',!caseRequest.caseInfo.externalAccessBlocked))
                            })
                            .catch((error) => {
                              setNotification({ type: "alert", msg: "It was not possible to unlock external access. Please try again." })
                              console.log(error)
                            })
                        }else{
                          jwtPostFetcher(getJwt())(`/api/admin/blockExternalAccess?requestId=${caseRequest.requestId}`)
                            .then((_response) => {
                              // setCaseRequest(_.set('externalAccessBlocked',!caseRequest.caseInfo.externalAccessBlocked))
                              setCaseRequest(_.update('caseInfo.externalAccessBlocked', (v) => !v))
                            })
                            .catch((error) => {
                              setNotification({ type: "alert", msg: "It was not possible to lock external access. Please try again." })
                              console.log(error)
                            })
                        }

                      }}
                    />
                    {caseRequest.canDeleteCase && <Li icon={"delete"} label={"Delete case"} onClick={openDeleteCase}/>}
                  </ContextMenu>
                }
              </Button>
              </div>


              {/* {providerInfo.readOnlyAccess && canAskForContribution && <ShareButtonDiv>
                <TextButtonContribution onClick={askContribution}> <Icon icon="collaboration"/>Request collaboration</TextButtonContribution>
              </ShareButtonDiv>} */}

              {providerInfo.readOnlyAccess && canShareCase && <ShareButtonDiv>
                <Button css={`width: 100%;`} highlight small onClick={shareCompleted} icon="share-filled">
                  Share case
                </Button>
              </ShareButtonDiv>}
              {/* <Button>Share</Button> */}
            </div>
            {caseRequest.state &&
            <>
              {caseRequest.state == CASE_ARCHIVED && <StepLink
                onClick={confirmRedirect}
                to="overview"
                // disabled={isCurrentStateBefore(CASE_DRAFT)}
                selected={step === "overview"}
              >
                <Icon icon="audit"/>
                Overview
              </StepLink>}
             <StepLink
                onClick={confirmRedirect}
                to="case-status"
                // disabled={isCurrentStateBefore(CASE_DRAFT)}
                selected={step === "case-status"}
              >
                <Icon icon="dashboard"/>
                Case status and activity
              </StepLink>
              <IfLocale not contains="LEGAL">
                {providerInfo?.caseReconciliationEnabled === true &&
                  <StepLink
                    onClick={confirmRedirect}
                    to="case-reconciliation"
                    // disabled={isCurrentStateBefore(CASE_DRAFT)}
                    selected={step === "case-reconciliation"}
                  >
                    <Icon icon="external-report"/>
                    Case reconciliation
                  </StepLink>
                }
              </IfLocale>
             <StepLink
                onClick={confirmRedirect}
                to="notes"
                // disabled={isCurrentStateBefore(CASE_DRAFT)}
                selected={step === "notes"}
              >
                <Icon icon="study-notes"/>
                Case notes
                <Icon icon="open-panel" className="secondaryActionIcon" onClick={(e) => {panelToggle.openWith(true); e.preventDefault();e.stopPropagation() }}/>
              </StepLink>
              <SidebarSeparator withTitle>
                <span>Actions</span>
              </SidebarSeparator>
              {messagingEnabled && <StepLink
                onClick={confirmRedirect}
                to="contact"
                selected={step === "contact"}
              >
                <Icon icon="messages"/>
                Messaging
                {caseRequest?.collaborationMessagesToRead > 0 && <span className="notificationNumber">
                  {caseRequest?.collaborationMessagesToRead}
                </span>}
              </StepLink>}
              <IfLocale not contains="LEGAL">
                {managePaymentsEnabled && <StepLink
                  onClick={confirmRedirect}
                  to={!step ? "." : "payment"}
                  selected={step === "payment"}
                >
                  <Icon icon="payment"/> Manage payments
                </StepLink>}

                {(providerInfo?.serviceTerms === true) &&
                  <StepLink
                    onClick={confirmRedirect}
                    to="agreements"
                    selected={step === "agreements"}
                  >
                    <Icon icon="sign-doc"/> Review agreements
                  </StepLink>
                }
              </IfLocale>

              <StepLink
                onClick={confirmRedirect}
                to="remote-consultation"
                selected={step === "remote-consultation"}
              >
                <Icon icon="video"/> Video consultation
              </StepLink>
              <SidebarSeparator withTitle>
                <span>Steps</span>
              </SidebarSeparator>
            </>
            }
          </>
          :
          <>
            <SidebarTitle>New opinion request</SidebarTitle>
          </>
        }
        <StepLink
          onClick={confirmRedirect}
          to="patient"
          // disabled={caseRequest.state && isCurrentStateBefore(PENDING_REQUEST)}
          selected={step === "patient" || isCreatingCase}
        ><Icon icon="user-line"/><FormattedMessage defaultMessage="Patient & contact details" description='RequestSidebar patient item label'/></StepLink>
        <StepLink
          onClick={confirmRedirect}
          to="releases"
          // disabled={isCurrentStateBefore(CASE_DRAFT)}
          selected={step === "releases"}
        ><Icon icon="edit-box"/> Locate medical records</StepLink>
        <StepLink
          onClick={confirmRedirect}
          to="records"
          // disabled={isCurrentStateBefore(CASE_DRAFT)}
          selected={step === "records"}
        ><Icon icon="dicom-no-text"/> Medical records</StepLink>
        <StepLink
          onClick={confirmRedirect}
          to="clinical"
          // disabled={isCurrentStateBefore(CASE_DRAFT)}
          selected={step === "clinical"}
        ><Icon icon="clinical-sheet"/><FormattedMessage defaultMessage="Clinical information" description='RequestSidebar clinical item label'/></StepLink>
        <StepLink
          onClick={confirmRedirect}
          to="expert"
          disabled={isCurrentStateBefore(PENDING_ASSIGNMENT)}
          selected={step === "expert" || step === "waiting" || step === "rejected"}
        ><Icon icon="physician-line"/> Expert assignment</StepLink>
        <StepLink
          onClick={confirmRedirect}
          to="review"
          disabled={isCurrentStateBefore(WAITING_MORE_INFORMATION) && !adminIsReviewingThisCase}
          selected={step === "review" || step === "waitingAdditional"}
        ><Icon icon="question-line"/> Expert review</StepLink>
        <StepLink
          onClick={confirmRedirect}
          to="report"
          disabled={isCurrentStateBefore(CASE_REVIEWED)}
          selected={step === "report" || step === "upload-report"}
        >{caseRequest.state === CASE_CLOSED ?
          <><Icon icon="checked-report"/> Final report</>
          : <><Icon icon="report"/> Case report</>}
        </StepLink>
        <IfLocale not contains="LEGAL">
          <StepLink
            onClick={confirmRedirect}
            to="continuing-care"
            // disabled={isCurrentStateBefore(CASE_REVIEWED)}
            selected={step === "continuing-care"}
          ><><Icon icon="hospital"/> Continuing care</>
          </StepLink>
        </IfLocale>
      </SidebarCard>
    </Sidebar>
    {directMessage.isOpen && (
      <ComposeDirectMessageDialog
        closeDialog={directMessage.close}
        caseRequest={caseRequest}
        setCaseRequest={setCaseRequest}
      />
    )}
  </SidebarContainer>
}
